import { Directive, ElementRef, HostListener, inject, Input, OnInit, Renderer2 } from '@angular/core'

/**
 * @internal
 */
@Directive({
  selector: 'input[type="checkbox"][gpeiCheckboxListItem]',
  exportAs: 'gpeiCheckboxListItem',
  standalone: true
})
export class CheckboxListItemDirective implements OnInit {

  private readonly _elementRef: ElementRef<HTMLInputElement> = inject(ElementRef)
  private readonly _renderer2 = inject(Renderer2)

  private _checked

  /**
   * The value that will be assigned when the host input element is checked
   */
  @Input()
  checkedValue: unknown = true

  /**
   * The value that will be assigned when the host input element is unchecked
   */
  @Input()
  uncheckedValue: unknown = false

  /**
   * Lifecycle hook for when the directive is initialized.
   *
   * Set up the initial value of the host input element.
   */
  ngOnInit(): void {
    this.updateValue(this._elementRef.nativeElement)
  }

  /**
   * Lifecycle hook for when the host input element is clicked.
   *
   * The original event will be halted, and then the value of the host input element will be
   * updated to either the {@see CheckboxListItemDirective.checkedValue } or the {@see CheckboxListItemDirective.uncheckedValue }
   * according to the current checked state.
   *
   * @param event The event that triggered this hook
   */
  @HostListener('input', [ '$event' ])
  onInput(event: InputEvent): void {
    let target = event.target as HTMLInputElement
    this.updateValue(target)
  }

  /**
   * Update the value to match the checked state of the item
   *
   * @param element The target element to update
   */
  updateValue(element: HTMLInputElement): void {
    let pendingValue = element.checked ?
      this.checkedValue :
      this.uncheckedValue

    this._renderer2.setProperty(element, 'value', pendingValue)
  }

  /**
   * The checked state of the host input element
   */
  @Input()
  set checked(value: boolean) {
    let element = this._elementRef.nativeElement
    this._checked = value
    this._renderer2.setProperty(element, 'checked', this._checked)
    this._renderer2.setAttribute(element, 'checked', this._checked)
  }

  get checked(): boolean {
    if (this._checked == null) {
      this._checked = this._elementRef.nativeElement.checked
    }

    return this._checked
  }

}
